import React, { useEffect, useState, useContext } from "react";
import { AppContext } from "../../store/AppContext";
import _ from "lodash";
import { TabView, TabPanel } from 'primereact/tabview';
import Label from "../../components/Label";
import SelectInput from "../../components/SelectInput";
import { ValueLabel } from "../../shared/models";
import { getAllConnectorLabels } from "./services/OrderService";
import { SalesOrderGridModel } from "./models";
import * as util from '../../shared/Utilities'
import { getFailedOrdersFromServer, getOrdersFromServer } from "../../dataServices";
import OrderGridComponent from "./components/OrderGridComponent";
import OrderGridErrorComponent from "./components/OrderGridErrorComponent";


export const useDebouncedEffect = (effect: any, deps: any, delay: number) => {
    useEffect(() => {
        const handler = setTimeout(() => effect(), delay);

        return () => clearTimeout(handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps || [], delay]);
}

const OrderGridContainer: React.FC = () => {
    let { State, Dispatch } = useContext(AppContext);
    const [selectedConnector, setSelectedConnector] = useState<string>(State?.selectedConnectorOnGrid ?? "");
    const [connectors, setConnectors] = useState<Array<ValueLabel>>([{ label: "", value: "" }]);
    // const [selectedOrders, setSelectedOrders] = useState<Array<SalesOrderGridModel>>();
    const [salesOrderData, setSalesOrderData] = useState<Array<SalesOrderGridModel>>([]);
    const [failedSalesOrderData, setFailedSalesOrderData] = useState<Array<SalesOrderGridModel>>([]);
    const [lastRecordId, setLastRecordId] = useState<string>();
    const [firstRecordId, setFirstRecordId] = useState<string>();
    const [failedFirstRecordId, setFailedFirstRecordId] = useState<string>('');
    const [failedLastRecordId, setFailedLastRecordId] = useState<string>('');

    const [activeTab, setActiveTab] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [fLoading, setFLoading] = useState<boolean>(false);
    const [isNextDisabled, setIsNextDisabled] = useState<boolean>(false);
    const [isPrevDisabled, setIsPrevDisabled] = useState<boolean>(false);
    const [isFailedOrderNextDisabled, setIsFailedOrderNextDisabled] = useState<boolean>(false);
    const [isFailedOrderPrevDisabled, setIsFailedOrderPrevDisabled] = useState<boolean>(false);
    const [loadedTabs, setLoadedTabs] = useState<Record<number, boolean>>({ 0: false, 1: false });
    const [isCalledOrders, setIsCalledOrders] = useState<boolean>(false);
    const [orderLazyParams, setOrdersLazyParams] = useState({
        first: 0, rows: 5, page: 1, sortField: "Date", sortOrder: -1,
        filters: undefined, pageMode: '', pageSize: 25,
    });
    const [failedOrderLazyParams, setFailedOrdersLazyParams] = useState({
        first: 0, rows: 5, page: 1, sortField: "Date", sortOrder: -1,
        filters: undefined, pageMode: '', pageSize: 15,
    });

    const filterFields = {
        netoOrderID: "IncomingOrder.OrderID",
        myobOrderID: "OutgoingOrder.Number",
        myobCustomer: "OutgoingOrder.Customer.Name",
        netoCustomer: "IncomingOrder.Username",
        myobStatus: "OutgoingOrder.Status",
        netoStatus: "IncomingOrder.OrderStatus",
        status: "Status",
        date: "Date",
    };

    // Fetch data when tab changes
    const handleTabChange = (e: any) => {
        const newTabIndex = e?.index;
        setActiveTab(newTabIndex);
        // Only fetch data if this tab hasn't been loaded before
        if (!loadedTabs[newTabIndex] && selectedConnector) {
            if (newTabIndex === 0) {
                fetchFailedOrders();
            } else {
                fetchOrders();
            }
            // Mark tab as loaded
            setLoadedTabs(prev => ({
                ...prev,
                [newTabIndex]: true
            }));
        }
    };

    const fetchFailedOrders = () => {
        if (State?.currentUser?.currentUserId && selectedConnector) {
            setFLoading(true);
            getFailedOrdersFromServer(
                State.currentUser.currentUserId,
                selectedConnector,
                failedOrderLazyParams.pageSize,
                failedOrderLazyParams.sortField,
                failedOrderLazyParams.sortOrder,
                failedFirstRecordId,
                failedLastRecordId,
                failedOrderLazyParams.pageMode,
                failedOrderLazyParams.filters
            ).subscribe({
                next: (e: any) => {
                    if (e?.ordersData && e?.ordersData?.length > 0) {
                        setFailedLastRecordId(e?.lastDocId);
                        setFailedFirstRecordId(e?.firstDocId);
                        setFailedSalesOrderData(e?.ordersData);
                        if (failedOrderLazyParams?.pageMode === 'ALL') {
                            setIsFailedOrderNextDisabled(true);
                            setIsFailedOrderPrevDisabled(true);
                        } else {
                            setIsFailedOrderNextDisabled(false);
                            setIsFailedOrderPrevDisabled(false);
                        }

                        setLoading(false);
                    }
                    else {
                        if (failedOrderLazyParams?.pageMode === 'ALL') {
                            setFailedSalesOrderData([]);
                            setIsFailedOrderNextDisabled(true);
                            setIsFailedOrderPrevDisabled(true);
                        } else if (failedOrderLazyParams?.pageMode === 'PREV') {
                            setIsFailedOrderPrevDisabled(true);
                            setFailedFirstRecordId("");
                        } else if (failedOrderLazyParams?.pageMode === 'NEXT') {
                            setIsFailedOrderNextDisabled(true);
                            setFailedLastRecordId("");
                        } else {
                            setFailedSalesOrderData([]);
                            setIsFailedOrderNextDisabled(true);
                            setIsFailedOrderPrevDisabled(true);
                        }
                    }
                    setFLoading(false);
                },
                error: (error: any) => {
                    console.error('Error fetching failed orders:', error);
                    setFLoading(false);
                }
            });
        }
    };

    // Load connectors on mount
    useEffect(() => {
        const loadConnectors = async () => {
            setLoading(true);
            try {
                const connectorsList = await getAllConnectorLabels(State?.currentUser?.currentUserId);
                setConnectors(connectorsList);
                if (connectorsList?.length > 0 && !selectedConnector) {
                    setSelectedConnector(connectorsList?.[0].value);
                }
            } catch (error) {
                console.error('Error loading connectors:', error);
            } finally {
                setLoading(false);
            }
        };
        if (State?.currentUser?.currentUserId) {
            loadConnectors();
        };
    }, [State?.currentUser?.currentUserId]);

    useEffect(() => {
        setOrdersLazyParams({
            first: 0,
            rows: 5,
            page: 1,
            sortField: "Date",
            sortOrder: -1,
            filters: undefined,
            pageMode: '',
            pageSize: 25,
        });
        setFailedOrdersLazyParams({
            first: 0,
            rows: 5,
            page: 1,
            sortField: "Date",
            sortOrder: -1,
            filters: undefined,
            pageMode: '',
            pageSize: 15,
        });
        setSalesOrderData([]);
        setFailedSalesOrderData([]);
    }, [selectedConnector]);

    useDebouncedEffect(() => {
        if (selectedConnector && !isCalledOrders) {
            setLoading(true);
            setIsCalledOrders(false);
            // Reset loaded tabs and data when connector changes
            setLoadedTabs({ 0: false, 1: false });
            setSalesOrderData([]);
            setFailedSalesOrderData([]);
            // Load data for the current tab
            if (activeTab === 0) {
                fetchFailedOrders();
            } else {
                fetchOrders();
            }
        };

        if (!_.isEmpty(selectedConnector) && isCalledOrders) {
            triggerFetchData();
        }
    }, [orderLazyParams, failedOrderLazyParams, selectedConnector], 1000);


    const triggerFetchData = () => {
        if (activeTab === 1) {
            // if (_.isEmpty(salesOrderData)) {
            setLoading(true);
            fetchOrders();
            // } else {
            //     const salesOrderDataCopy = [...salesOrderData];
            //     setSalesOrderData(salesOrderDataCopy);
            // }
        } else if (activeTab === 0) {
            setLoading(true);
            fetchFailedOrders();
        }
        setLoadedTabs(prev => ({
            ...prev,
            [activeTab]: true
        }));
    };

    const fetchOrders = () => {
        if (State?.currentUser?.currentUserId && selectedConnector) {
            let filtersArray: { key: string; value: any; }[] = [];
            if (orderLazyParams?.filters) {
                const filters: any = orderLazyParams.filters;
                if (filters?.myobCustomer) {
                    if (!_.isEmpty(filters?.myobCustomer?.value))
                        filtersArray.push({ key: filterFields.myobCustomer, value: filters?.myobCustomer?.value })
                }

                if (filters?.netoCustomer) {
                    if (!_.isEmpty(filters?.netoCustomer?.value))
                        filtersArray.push({ key: filterFields.netoCustomer, value: filters?.netoCustomer?.value })
                }

                if (filters?.netoOrderId) {
                    if (!_.isEmpty(filters?.netoOrderId?.value))
                        filtersArray.push({ key: filterFields.netoOrderID, value: filters?.netoOrderId?.value })
                }

                if (filters?.myobOrderId) {
                    if (!_.isEmpty(filters?.myobOrderId?.value))
                        filtersArray.push({ key: filterFields.myobOrderID, value: filters?.myobOrderId?.value })
                }

                if (filters?.netoOrderStatus) {
                    if (!_.isEmpty(filters?.netoOrderStatus?.value))
                        filtersArray.push({ key: filterFields.netoStatus, value: filters?.netoOrderStatus?.value })
                }

                if (filters?.myobOrderStatus) {
                    if (!_.isEmpty(filters?.myobOrderStatus?.value))
                        filtersArray.push({ key: filterFields.myobStatus, value: filters?.myobOrderStatus?.value })
                }

                if (filters['status.orderStatus']) {
                    if (!_.isEmpty(filters['status.orderStatus']?.value))
                        filtersArray.push({ key: filterFields.status, value: filters['status.orderStatus']?.value })
                }

                if (filters['datePlaced']) {
                    if (filters['datePlaced']?.value)
                        filtersArray.push({ key: filterFields.date, value: util.getUserTimezoneStartAndEndOfADay(filters['datePlaced']?.value) });
                }
            }

            getOrdersFromServer(State.currentUser.currentUserId,
                selectedConnector, orderLazyParams?.pageSize,
                orderLazyParams?.sortField, orderLazyParams?.sortOrder,
                firstRecordId, lastRecordId, orderLazyParams.pageMode, filtersArray).subscribe((e: any) => {
                    if (e?.ordersData && e?.ordersData?.length > 0) {
                        setLastRecordId(e?.lastDocId);
                        setFirstRecordId(e?.firstDocId);
                        setSalesOrderData(e?.ordersData);
                        if (orderLazyParams?.pageMode === 'ALL') {
                            setIsNextDisabled(true);
                            setIsPrevDisabled(true);
                        } else {
                            setIsNextDisabled(false);
                            setIsPrevDisabled(false);
                        }

                        setLoading(false);
                    }
                    else {
                        if (orderLazyParams?.pageMode === 'ALL') {
                            setSalesOrderData([]);
                            setIsNextDisabled(true);
                            setIsPrevDisabled(true);
                        } else if (orderLazyParams?.pageMode === 'PREV') {
                            setIsPrevDisabled(true);
                            setFirstRecordId("");
                        } else if (orderLazyParams?.pageMode === 'NEXT') {
                            setIsNextDisabled(true);
                            setLastRecordId("");
                        } else {
                            setSalesOrderData([]);
                            setIsNextDisabled(true);
                            setIsPrevDisabled(true);
                        }

                        setLoading(false);
                    }
                });

            util.getTimezoneFromFirebase(
                State?.currentUser?.isAdminUser && localStorage.SelectedIdByAdmin ? localStorage.SelectedIdByAdmin : State.currentUser.currentUserId,
                selectedConnector
            );
        };
    };

    return (
        <div className="container-fluid">
            <div className="row mt-5">
                <div className="col-3">
                    <Label text={"Select Connector: "} />
                </div>
                <div className="col-9">
                    <SelectInput
                        options={connectors}
                        selectedOptionValue={selectedConnector}
                        onChange={(connector: string) => {
                            Dispatch({
                                Type: "SET_SELECTED_CONNECTOR",
                                Payload: { id: connector },
                            });
                            setSelectedConnector(connector)
                            // setSelectedOrders([])
                        }}
                    />
                </div>
            </div>
            <TabView activeIndex={activeTab} onTabChange={handleTabChange}>
                <TabPanel header={<b>Failed Orders</b>}>
                    <OrderGridErrorComponent
                        loading={fLoading}
                        orderData={failedSalesOrderData}
                        selectedConnector={selectedConnector}
                        lazyParams={failedOrderLazyParams}
                        setLazyParams={setFailedOrdersLazyParams}
                        clearPaginationRecord={() => {
                            setFailedFirstRecordId('');
                            setFailedLastRecordId('');
                        }}
                        isNextDisabled={isFailedOrderNextDisabled}
                        isPrevDisabled={isFailedOrderPrevDisabled}
                    />
                </TabPanel>
                <TabPanel header={<b>Processed Orders</b>}>
                    <OrderGridComponent
                        orderData={salesOrderData}
                        isNextDisabled={isNextDisabled}
                        isPrevDisabled={isPrevDisabled}
                        lazyParams={orderLazyParams}
                        loading={loading}
                        // setLoading={setLoading}
                        selectedConnector={selectedConnector}
                        setLazyParams={setOrdersLazyParams}
                        setStartAfterRecord={setLastRecordId}
                        setStartBeforeRecord={setFirstRecordId}
                    />
                </TabPanel>
            </TabView>
        </div>
    );
}


export default OrderGridContainer
